import React, { useState, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  FormControlLabel,
  Switch,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  useMediaQuery,
  Paper,
  Collapse,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Close,
  ChevronLeft,
  Add,
  FolderOpen,
  Assignment,
  Folder,
  Share,
  PersonAdd,
  PlaylistAddCheck,
  ExpandLess,
  ExpandMore,
  Public,
  Check,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from 'react-query';
import client from '../../../../../API';
import { getApplicantDisplayName, getOrganizerTypeIcon, getOrganizerTypeColor } from './castingCallHelpers';
import { useNavigate } from 'react-router-dom';

const OrganizerSidebar = memo(({ 
  castingCallId, 
  organizers, 
  applicants,
  castingCall,
  open, 
  onClose, 
  onRefresh 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();


  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [expandedOrganizer, setExpandedOrganizer] = useState(null);

  const [newOrganizer, setNewOrganizer] = useState({
    name: '',
    description: '',
    link_type: 'deliverables',
    applicants_can_view: false,
    selected_can_upload: true,
    visible_after_selection: true,
  });

  const [assignmentSettings, setAssignmentSettings] = useState({
    applicant_ids: [],
    permission_level: 'read',
    can_upload: false,
  });

  const createOrganizerMutation = useMutation(
    (data) => client.castingCalls.createOrganizer(castingCallId, data),
    {
      onSuccess: () => {
        setCreateDialogOpen(false);
        setNewOrganizer({
          name: '',
          description: '',
          link_type: 'deliverables',
          applicants_can_view: false,
          selected_can_upload: true,
          visible_after_selection: true,
        });
        onRefresh();
        queryClient.invalidateQueries(['castingCall', castingCallId]);
      },
      onError: (error) => {
        alert(error?.response?.data?.error || 'Failed to create organizer');
      },
    }
  );

  const assignOrganizerMutation = useMutation(
    (data) => client.castingCalls.assignOrganizerToApplicants(
      castingCallId, 
      selectedOrganizer.organizer_id, 
      data
    ),
    {
      onSuccess: () => {
        setAssignDialogOpen(false);
        setSelectedOrganizer(null);
        setAssignmentSettings({
          applicant_ids: [],
          permission_level: 'read',
          can_upload: false,
        });
        onRefresh();
        queryClient.invalidateQueries(['castingCall', castingCallId]);
      },
      onError: (error) => {
        alert(error?.response?.data?.error || 'Failed to assign organizer');
      },
    }
  );

  const handleCreateOrganizer = () => {
    if (!newOrganizer.name.trim()) {
      alert('Organizer name is required');
      return;
    }
    createOrganizerMutation.mutate(newOrganizer);
  };

  const handleAssignOrganizer = () => {
    if (assignmentSettings.applicant_ids.length === 0) {
      alert('Please select at least one applicant');
      return;
    }
    assignOrganizerMutation.mutate(assignmentSettings);
  };

  const iconMap = {
    Assignment,
    Folder,
    Share,
  };

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Organizers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Create Organizer">
            <IconButton 
              size="small" 
              onClick={() => setCreateDialogOpen(true)}
              color="primary"
            >
              <Add />
            </IconButton>
          </Tooltip>
          {isMobile && (
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {organizers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FolderOpen sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No organizers yet
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create First Organizer
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {organizers.map((org) => {
              const IconComponent = iconMap[getOrganizerTypeIcon(org.link_type)] || Folder;
              const colorType = getOrganizerTypeColor(org.link_type);
              
              return (
                <Paper
                  key={org.id}
                  sx={{
                    mb: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${theme.palette[colorType].main}`,
                  }}
                >
                  <ListItemButton
                    onClick={() => setExpandedOrganizer(expandedOrganizer === org.id ? null : org.id)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${colorType}.light` }}>
                        <IconComponent />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {org.display_name || org.organizer?.name || 'Untitled'}
                          </Typography>
                          {org.is_primary && (
                            <Chip label="Primary" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {org.organizer?.file_count || 0} files • {org.link_type}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                            {org.applicants_can_view && (
                              <Chip label="Public" size="small" icon={<Public />} />
                            )}
                            {org.selected_can_upload && (
                              <Chip label="Uploadable" size="small" icon={<Check />} />
                            )}
                          </Stack>
                        </Stack>
                      }
                    />
                    <IconButton size="small">
                      {expandedOrganizer === org.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </ListItemButton>

                  <Collapse in={expandedOrganizer === org.id}>
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      
                      {org.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {org.description}
                        </Typography>
                      )}

                      <Stack spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => {
                            setSelectedOrganizer(org);
                            setAssignDialogOpen(true);
                          }}
                          fullWidth
                        >
                          Assign to Applicants
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlaylistAddCheck />}
                          onClick={() => {
                              navigate(`/organizers/${org.organizer_id}/checklist`);

                          }}
                          fullWidth
                        >
                          Manage Checklist
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<FolderOpen />}
                          onClick={() => {
                             navigate(`/organizers/${org.organizer_id}`);
                          }}
                          fullWidth
                        >
                          View Files
                        </Button>
                      </Stack>

                      {org.assigned_applicants && org.assigned_applicants.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Assigned to:
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {org.assigned_applicants.map((app) => (
                              <Chip
                                key={app.id || app.username}
                                label={getApplicantDisplayName(app, castingCall)}
                                size="small"
                                avatar={<Avatar src={app.pfphref}>{getApplicantDisplayName(app, castingCall)[0]}</Avatar>}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: { width: '100%', maxWidth: 400 }
          }}
        >
          {sidebarContent}
        </Drawer>

        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Organizer</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Organizer Name"
              fullWidth
              variant="outlined"
              value={newOrganizer.name}
              onChange={(e) => setNewOrganizer({ ...newOrganizer, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={newOrganizer.description}
              onChange={(e) => setNewOrganizer({ ...newOrganizer, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={newOrganizer.link_type}
                onChange={(e) => setNewOrganizer({ ...newOrganizer, link_type: e.target.value })}
                label="Type"
              >
                <MenuItem value="deliverables">Deliverables</MenuItem>
                <MenuItem value="brief">Brief</MenuItem>
                <MenuItem value="shared">Shared</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={newOrganizer.applicants_can_view}
                  onChange={(e) => setNewOrganizer({ ...newOrganizer, applicants_can_view: e.target.checked })}
                />
              }
              label="All applicants can view"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newOrganizer.selected_can_upload}
                  onChange={(e) => setNewOrganizer({ ...newOrganizer, selected_can_upload: e.target.checked })}
                />
              }
              label="Selected applicant can upload"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateOrganizer}
              variant="contained"
              disabled={!newOrganizer.name.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Organizer to Applicants</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Organizer: {selectedOrganizer?.display_name || selectedOrganizer?.organizer?.name}
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Applicants:
            </Typography>
            <List>
              {applicants.map((applicant) => (
                <ListItem key={applicant.id || applicant.username} disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={assignmentSettings.applicant_ids.includes(applicant.id || applicant.username)}
                        onChange={(e) => {
                          const id = applicant.id || applicant.username;
                          if (e.target.checked) {
                            setAssignmentSettings({
                              ...assignmentSettings,
                              applicant_ids: [...assignmentSettings.applicant_ids, id],
                            });
                          } else {
                            setAssignmentSettings({
                              ...assignmentSettings,
                              applicant_ids: assignmentSettings.applicant_ids.filter(a => a !== id),
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={applicant.pfphref} sx={{ width: 32, height: 32 }}>
                          {getApplicantDisplayName(applicant, castingCall)[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {getApplicantDisplayName(applicant, castingCall)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Permission Level</InputLabel>
              <Select
                value={assignmentSettings.permission_level}
                onChange={(e) => setAssignmentSettings({ ...assignmentSettings, permission_level: e.target.value })}
                label="Permission Level"
              >
                <MenuItem value="read">Read Only</MenuItem>
                <MenuItem value="write">Read & Upload</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={assignmentSettings.can_upload}
                  onChange={(e) => setAssignmentSettings({ ...assignmentSettings, can_upload: e.target.checked })}
                />
              }
              label="Can upload files"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAssignOrganizer}
              variant="contained"
              disabled={assignmentSettings.applicant_ids.length === 0}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          right: 0,
          top: 64,
          height: 'calc(100vh - 64px)',
          width: open ? 400 : 60,
          transition: 'width 0.3s',
          zIndex: 1200,
          overflow: 'hidden',
        }}
      >
        {open ? (
          sidebarContent
        ) : (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Tooltip title="Open Organizers" placement="left">
              <IconButton onClick={() => onClose()} color="primary">
                <ChevronLeft />
              </IconButton>
            </Tooltip>
            <Divider sx={{ my: 1 }} />
            <Tooltip title="Create Organizer" placement="left">
              <IconButton onClick={() => setCreateDialogOpen(true)} size="small">
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Paper>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organizer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organizer Name"
            fullWidth
            variant="outlined"
            value={newOrganizer.name}
            onChange={(e) => setNewOrganizer({ ...newOrganizer, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newOrganizer.description}
            onChange={(e) => setNewOrganizer({ ...newOrganizer, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={newOrganizer.link_type}
              onChange={(e) => setNewOrganizer({ ...newOrganizer, link_type: e.target.value })}
              label="Type"
            >
              <MenuItem value="deliverables">Deliverables</MenuItem>
              <MenuItem value="brief">Brief</MenuItem>
              <MenuItem value="shared">Shared</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={newOrganizer.applicants_can_view}
                onChange={(e) => setNewOrganizer({ ...newOrganizer, applicants_can_view: e.target.checked })}
              />
            }
            label="All applicants can view"
          />
          <FormControlLabel
            control={
              <Switch
                checked={newOrganizer.selected_can_upload}
                onChange={(e) => setNewOrganizer({ ...newOrganizer, selected_can_upload: e.target.checked })}
              />
            }
            label="Selected applicant can upload"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateOrganizer}
            variant="contained"
            disabled={!newOrganizer.name.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Organizer to Applicants</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Organizer: {selectedOrganizer?.display_name || selectedOrganizer?.organizer?.name}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select Applicants:
          </Typography>
          <List>
            {applicants.map((applicant) => (
              <ListItem key={applicant.id || applicant.username} disablePadding>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={assignmentSettings.applicant_ids.includes(applicant.id || applicant.username)}
                      onChange={(e) => {
                        const id = applicant.id || applicant.username;
                        if (e.target.checked) {
                          setAssignmentSettings({
                            ...assignmentSettings,
                            applicant_ids: [...assignmentSettings.applicant_ids, id],
                          });
                        } else {
                          setAssignmentSettings({
                            ...assignmentSettings,
                            applicant_ids: assignmentSettings.applicant_ids.filter(a => a !== id),
                          });
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={applicant.pfphref} sx={{ width: 32, height: 32 }}>
                        {getApplicantDisplayName(applicant, castingCall)[0]}
                      </Avatar>
                      <Typography variant="body2">
                        {getApplicantDisplayName(applicant, castingCall)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Permission Level</InputLabel>
            <Select
              value={assignmentSettings.permission_level}
              onChange={(e) => setAssignmentSettings({ ...assignmentSettings, permission_level: e.target.value })}
              label="Permission Level"
            >
              <MenuItem value="read">Read Only</MenuItem>
              <MenuItem value="write">Read & Upload</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={assignmentSettings.can_upload}
                onChange={(e) => setAssignmentSettings({ ...assignmentSettings, can_upload: e.target.checked })}
              />
            }
            label="Can upload files"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignOrganizer}
            variant="contained"
            disabled={assignmentSettings.applicant_ids.length === 0}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

OrganizerSidebar.displayName = 'OrganizerSidebar';

export default OrganizerSidebar;